export interface BotPayloadSummaryLine {
  label: string;
  value: string;
}

type PortLike = { name?: string | null; country?: string | null } | null;
type ContainerLike = {
  reference?: string | null;
  status?: string | null;
  bl_number?: string | null;
  etd?: string | null;
  delayed_etd?: string | null;
  eta?: string | null;
  track_and_trace?: string | null;
  origin_port?: PortLike;
  destination_port?: PortLike;
} | null;

function portLabel(port: unknown): string | null {
  if (!port || typeof port !== "object") return null;
  const p = port as { name?: string | null; country?: string | null };
  if (!p.name) return null;
  return p.country ? `${p.name} (${p.country})` : p.name;
}

function dateLabel(value: unknown): string | null {
  if (typeof value !== "string" || !value) return null;
  return value.slice(0, 10);
}

/** Short human summary of what the bot context JSON contains. */
export function summarizeBotBookingPayload(
  payload: Record<string, unknown>,
): BotPayloadSummaryLine[] {
  const transport = payload.transport_type as
    | { name?: string | null; abbreviation?: string | null }
    | null
    | undefined;
  const bookingType = payload.booking_type as { name?: string | null } | null;
  const incoterm = payload.booking_incoterm as { name?: string | null } | null;
  const container = payload.container as ContainerLike;
  const supplier = payload.supplier as { name?: string | null } | null;
  const productSummary = payload.product_summary as {
    count?: number;
    total_cartons?: number;
    total_cbm?: number;
  } | null;
  const documents = payload.documents as {
    has_commercial_invoice?: boolean;
    invoice_count?: number;
    other_document_count?: number;
  } | null;
  const lastMile = Array.isArray(payload.last_mile_shipments)
    ? payload.last_mile_shipments
    : [];
  const timeline = payload.timeline as {
    comments?: unknown[];
    notifications?: unknown[];
  } | null;
  const deliveryDetails = Array.isArray(payload.delivery_details)
    ? payload.delivery_details
    : [];

  const route = [
    portLabel(payload.origin_port) || portLabel(container?.origin_port),
    portLabel(payload.destination_port) ||
      portLabel(container?.destination_port),
  ]
    .filter(Boolean)
    .join(" → ");

  const lines: BotPayloadSummaryLine[] = [
    {
      label: "Booking",
      value: `${String(payload.eff_reference ?? payload.id)} · #${payload.id} · ${
        payload.status ?? "—"
      }`,
    },
    {
      label: "Transport",
      value:
        [
          transport?.name || transport?.abbreviation || null,
          bookingType?.name || null,
          incoterm?.name || null,
          payload.service_level ? `service: ${payload.service_level}` : null,
        ]
          .filter(Boolean)
          .join(" · ") || "—",
    },
    {
      label: "Route",
      value: route || "—",
    },
    {
      label: "Customer ETA",
      value: dateLabel(payload.customer_eta) || "—",
    },
    {
      label: "Container",
      value: container
        ? [
            container.reference || "no ref",
            container.status || null,
            container.bl_number ? `BL ${container.bl_number}` : null,
            dateLabel(container.delayed_etd || container.etd)
              ? `ETD ${dateLabel(container.delayed_etd || container.etd)}`
              : null,
            dateLabel(container.eta) ? `ETA ${dateLabel(container.eta)}` : null,
            container.track_and_trace ? "has track & trace" : null,
          ]
            .filter(Boolean)
            .join(" · ")
        : "none",
    },
    {
      label: "Delivery",
      value:
        payload.delivery_type === "address"
          ? `${deliveryDetails.length} address${
              deliveryDetails.length === 1 ? "" : "es"
            }`
          : `storage${
              payload.storage_option ? ` (${payload.storage_option})` : ""
            }`,
    },
    {
      label: "Supplier",
      value: supplier?.name || "—",
    },
    {
      label: "Products",
      value: productSummary
        ? `${productSummary.count ?? 0} products · ${
            productSummary.total_cartons ?? 0
          } cartons · ${Number(productSummary.total_cbm ?? 0).toFixed(2)} CBM`
        : "—",
    },
    {
      label: "Documents",
      value: documents
        ? [
            documents.has_commercial_invoice ? "commercial invoice" : null,
            (documents.invoice_count ?? 0) > 0
              ? `${documents.invoice_count} invoice(s)`
              : null,
            (documents.other_document_count ?? 0) > 0
              ? `${documents.other_document_count} other`
              : null,
          ]
            .filter(Boolean)
            .join(" · ") || "none"
        : "—",
    },
    {
      label: "Last-mile",
      value:
        lastMile.length > 0
          ? `${lastMile.length} Sendcloud shipment(s)`
          : "none",
    },
    {
      label: "LeanCustoms",
      value: (() => {
        const customs = payload.customs as {
          lean_customs?: {
            available?: boolean;
            order?: { reference?: string | null; status?: string | null } | null;
            declaration?: {
              mrn?: string | null;
              dms_status_label?: string | null;
              reference?: string | null;
            } | null;
            amounts?: {
              utb_payment?: string | null;
              ttw_surety?: string | null;
            };
            documents?: unknown[];
            warnings?: string[];
          } | null;
        } | null;
        const lc = customs?.lean_customs;
        if (!lc) return "not configured / not loaded";
        if (!lc.available) {
          return lc.warnings?.length
            ? `unavailable (${lc.warnings[0]})`
            : "no match";
        }
        return [
          lc.order?.reference ? `order ${lc.order.reference}` : null,
          lc.order?.status || null,
          lc.declaration?.dms_status_label
            ? `decl ${lc.declaration.dms_status_label}`
            : null,
          lc.declaration?.mrn ? `MRN ${lc.declaration.mrn}` : null,
          lc.amounts?.utb_payment
            ? `UTB ${lc.amounts.utb_payment}`
            : null,
          lc.amounts?.ttw_surety
            ? `TTW ${lc.amounts.ttw_surety}`
            : null,
          Array.isArray(lc.documents) && lc.documents.length > 0
            ? `${lc.documents.length} docs`
            : null,
        ]
          .filter(Boolean)
          .join(" · ") || "loaded";
      })(),
    },
    {
      label: "Master B/L",
      value: (() => {
        const mc = payload.master_container as {
          master?: {
            reference?: string | null;
            status?: string | null;
            effective_etd?: string | null;
            eta_customer?: string | null;
            track_and_trace?: string | null;
          };
          same_company_sibling_bookings?: unknown[];
          linked_container_count?: number;
        } | null;
        if (!mc?.master) return "none";
        return [
          mc.master.reference || `master #${(mc.master as { id?: number }).id ?? "?"}`,
          mc.master.status || null,
          mc.master.effective_etd
            ? `ETD ${String(mc.master.effective_etd).slice(0, 10)}`
            : null,
          mc.master.eta_customer
            ? `ETA cust ${String(mc.master.eta_customer).slice(0, 10)}`
            : null,
          mc.master.track_and_trace ? "has T&T" : null,
          `${mc.linked_container_count ?? 0} container(s)`,
          `${mc.same_company_sibling_bookings?.length ?? 0} same-company sibling booking(s)`,
        ]
          .filter(Boolean)
          .join(" · ");
      })(),
    },
    {
      label: "Timeline",
      value: `${timeline?.comments?.length ?? 0} comments · ${
        timeline?.notifications?.length ?? 0
      } notifications`,
    },
  ];

  if (payload.notes) {
    lines.push({
      label: "Notes",
      value: String(payload.notes).slice(0, 160),
    });
  }

  return lines;
}
